import { createSelector } from '@reduxjs/toolkit';
import type { GenericState } from '../../../types';
import type { RootState } from '../../store';
import {
  createGenericAsyncThunk,
  createGenericBuilderCases,
  createGenericSlice,
} from '../generic';
import { transformStringToPath } from '../../../helpers/transformStringToPath';

// From external json structure, can be changed unexpectedly
export interface IGFEntryProfileImageProps {
  caption: string;
  url: string;
  thumb_200: string;
  thumb_450: string;
  thumb_1000: string;
}

export interface IGFEntryProfileVideoProps {
  caption: string;
  type: string;
  videoid: string;
  url: string;
  embed: string;
}

export interface IGFEntryProfileProps {
  id: string;
  creator: string;
  name: string;
  description: string;
  foundCrowd: string;
  foundOther: string;
  image: IGFEntryProfileImageProps;
  isStudent: string;
  school: string;
  twitter: string;
  url: string;
  video: IGFEntryProfileVideoProps;
}

export const fetchIGFEntryByYear = createGenericAsyncThunk<
  IGFEntryProfileProps[],
  { dataUrl: string }
>(
  'igfEntries/fetchIGFEntryByYear',
  async ({ dataUrl }, { extra, rejectWithValue }) => {
    const response = await fetch('/api/v1/igf/event/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain, *!/!*',
      },
      body: JSON.stringify({ dataUrl }),
    });
    const data = await response.json();

    if (response.status !== 200) {
      return rejectWithValue(extra.processRejectedValue(response, data));
    }

    return data.entries as IGFEntryProfileProps[];
  },
);

export const selectEntriesData = createSelector(
  [(state: RootState) => state.igfEntries],
  (data) => data,
);

export const selectEntryDetails = createSelector(
  [
    (state: RootState) => selectEntriesData(state),
    (state: RootState, entrySlugName: string) => entrySlugName,
  ],
  (entries, entrySlugName) => {
    if (entries.status !== 'fulfilled') return {};

    const entriesList = entries.data as IGFEntryProfileProps[];

    const profileData = entriesList.find(
      (profile) => transformStringToPath(profile.name) === entrySlugName,
    );
    if (!profileData) return {};

    return profileData;
  },
);

const igfEntriesSlice = createGenericSlice({
  name: 'igfEntries',
  initialState: {
    status: 'idle',
    data: {},
  } as GenericState<IGFEntryProfileProps[]>,
  reducers: {},
  extraReducers: (builder) => {
    createGenericBuilderCases(builder, fetchIGFEntryByYear);
  },
});

export const selectIGFEntriesStatus = (state: RootState) =>
  state.igfEntries.status;

export const selectIGFEntriesData = (state: RootState) => state.igfEntries.data;

export default igfEntriesSlice.reducer;
